'use client';

import React, { useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { OAUTH_PROVIDER, OAuthProvider, OAuthIntent, initiateOAuthLogin } from '@/lib/oauth';

import styles from './OAuthButtons.module.css';

interface OAuthButtonsProps {
  intent: OAuthIntent; // Required intent for the OAuth flow (login, register, link_account)
  inviteCode?: string; // Optional invite code for registration flow
  returnUrl?: string; // Optional URL to return to after OAuth flow
}

/** Starts the selected OAuth flow for login, registration, or account linking. */
export const OAuthButtons: React.FC<OAuthButtonsProps> = ({ intent, inviteCode, returnUrl }) => {
  // With the required intent prop, we don't need a getIntent function anymore
  const [error, setError] = useState<string>('');
  const theme = useTheme();
  // Note: Accessing theme.palette.mode is OK for checking the current mode
  // But for colors in styles, use CSS variables (e.g., var(--mui-palette-background-default))
  const isDarkMode = theme.palette.mode === 'dark';

  const handleOAuthLogin = async (provider: OAuthProvider) => {
    try {
      // Clear any previous error
      setError('');

      // Get authorization URL from the server
      // The server maintains all state in the session
      const { url, rpcError } = await initiateOAuthLogin(provider, intent, inviteCode, returnUrl);

      // Check if there was an RPC error
      if (rpcError || !url) {
        setError('Failed to connect to authentication provider. Please try again.');
        return;
      }

      // The URL from the server already has all necessary parameters
      // Redirect to the OAuth provider directly
      window.location.href = url;
    } catch (error) {
      console.error('OAuth login error:', error);
      setError('Failed to connect to authentication provider. Please try again.');
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <Box className={styles.oauthDivider} sx={{ color: 'text.secondary' }}>
        <Box className={styles.oauthDividerLine} sx={{ backgroundColor: 'divider' }} />
        <Typography className={styles.oauthDividerText} variant="body2">
          {intent === OAuthIntent.LinkAccount ? 'Connect' : 'OR'}
        </Typography>
        <Box className={styles.oauthDividerLine} sx={{ backgroundColor: 'divider' }} />
      </Box>

      <div className={styles.oauthButtonContainer}>
        {/* Google Sign-In button - following official brand guidelines */}
        <Button
          className={`${styles.googleSigninButton} ${isDarkMode ? styles.darkModeButton : styles.lightModeButton}`}
          onClick={() => handleOAuthLogin(OAUTH_PROVIDER.Google)}
          type="button"
          aria-label={
            intent === OAuthIntent.LinkAccount ? 'Sign in with Google' : 'Continue with Google'
          }
          fullWidth
          startIcon={
            <div className={styles.googleSigninIcon}>
              <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                />
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                />
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                />
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                />
                <path fill="none" d="M0 0h48v48H0z" />
              </svg>
            </div>
          }
        >
          {intent === OAuthIntent.LinkAccount ? 'Sign in with Google' : 'Continue with Google'}
        </Button>

        {/* Facebook Login button - following brand guidelines */}
        <Button
          className={`${styles.facebookSigninButton} ${isDarkMode ? styles.darkModeButton : styles.lightModeButton}`}
          onClick={() => handleOAuthLogin(OAUTH_PROVIDER.Meta)}
          type="button"
          aria-label={
            intent === OAuthIntent.LinkAccount ? 'Sign in with Facebook' : 'Continue with Facebook'
          }
          fullWidth
          startIcon={
            <div className={styles.facebookSigninIcon}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 17.9895 4.3882 22.954 10.125 23.8542V15.4688H7.07812V12H10.125V9.35625C10.125 6.34875 11.9166 4.6875 14.6576 4.6875C15.9701 4.6875 17.3438 4.92188 17.3438 4.92188V7.875H15.8306C14.34 7.875 13.875 8.80008 13.875 9.75V12H17.2031L16.6711 15.4688H13.875V23.8542C19.6118 22.954 24 17.9895 24 12Z"
                  fill="#1877F2"
                />
              </svg>
            </div>
          }
        >
          {intent === OAuthIntent.LinkAccount ? 'Sign in with Facebook' : 'Continue with Facebook'}
        </Button>
      </div>
      {error && (
        <Typography role="alert" className={styles.errorMessage} color="error">
          {error}
        </Typography>
      )}
    </div>
  );
};
